/**
 * `MandateSigningCard` — the animated ed25519 signature-preview strip
 * rendered on Intent / Cart / Pay phases (mandate-emitting steps).
 *
 * ADR-0159 §4 introduces the tier gate:
 *
 *   `'verified'`   — renders the full animated ed25519 art (djb2 /
 *                    mulberry32 display artifact — no cryptographic
 *                    meaning; display only)
 *   `'identified'` — renders an honest one-line note: "signature preview
 *                    omitted — no `public_keys` published for this
 *                    identity yet." NO animated art.
 *   `'unknown'` /
 *   `'blocked'`    — renders the existing "NO KEY" state (unchanged
 *                    from ADR-0111).
 *
 * The gate is on the tier returned by `useAgentRegistry`, NOT on a
 * client-authored `verified: true` boolean. This is the honesty fix in
 * §4 (Phase 1 has no `public_keys` wired for any named agent).
 */

import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Check, FileSignature, Info, KeyRound } from 'lucide-react';

import { useC } from '../../../lib/theme.js';
import type { AgentDef, VerificationTier } from '../roster.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export type MandateTone = 'sky' | 'amber' | 'lime';

export interface MandateKind {
  kind: string;
  code: string;
  tone: MandateTone;
}

export function mandateKindFor(id: string): MandateKind | null {
  if (id === 'intent') return { kind: 'IntentMandate', code: 'im', tone: 'sky' };
  if (id === 'cart') return { kind: 'CartMandate', code: 'cm', tone: 'amber' };
  if (id === 'pay') return { kind: 'PaymentMandate', code: 'pm', tone: 'lime' };
  return null;
}

export interface MandateSigningCardProps {
  mandate: MandateKind;
  agent: AgentDef;
  animate: boolean;
  /** Resolved by `useAgentRegistry` — never trust a client boolean. */
  tier: VerificationTier;
}

export function MandateSigningCard({ mandate, agent, animate, tier }: MandateSigningCardProps): JSX.Element {
  const C = useC();
  const [signed, setSigned] = useState(!animate);

  const toneDim: string = { sky: C.skyDim, amber: C.amberDim, lime: C.limeDim }[mandate.tone];
  const toneFg: string = { sky: C.skyFg, amber: C.amberFg, lime: C.limeFg }[mandate.tone];

  useEffect(() => {
    if (!animate) {
      setSigned(true);
      return;
    }
    setSigned(false);
    const t = setTimeout(() => setSigned(true), 700);
    return () => clearTimeout(t);
  }, [animate, mandate.kind]);

  // Deterministic display id derived from a djb2 hash of the seed.
  const mandateId = useMemo(() => {
    const seed = `${mandate.code}_${agent.id}_${mandate.kind}`;
    let h = 5381;
    for (let i = 0; i < seed.length; i++) h = ((h << 5) + h + seed.charCodeAt(i)) >>> 0;
    return `${mandate.code}_${h.toString(16).padStart(8, '0').slice(0, 8)}`;
  }, [agent.id, mandate.kind, mandate.code]);

  // Mulberry32 seeded from the mandateId — a stable, realistic-looking 64-char
  // hex string for display only (no cryptographic meaning).
  const sigPreview = useMemo(() => {
    let seed = 2166136261;
    for (let i = 0; i < mandateId.length; i++) {
      seed ^= mandateId.charCodeAt(i);
      seed = Math.imul(seed, 16777619) >>> 0;
    }
    const chars = '0123456789abcdef';
    let s = '0x';
    for (let i = 0; i < 64; i++) {
      seed += 0x6d2b79f5;
      let t = seed;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      const r = ((t ^ (t >>> 14)) >>> 0) & 15;
      s += chars[r];
    }
    return s;
  }, [mandateId]);

  // ── Tier gate ────────────────────────────────────────────────────────
  // 'identified' — the Phase-1 posture for every named agent. Render an
  // honest one-line note instead of the animated art.
  if (tier === 'identified') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        className="overflow-hidden"
        style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 3 }}
      >
        <div className="px-3 py-2 flex items-center gap-2 flex-wrap">
          <FileSignature size={11} color={C.muted} />
          <span style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1.5, fontWeight: 600 }}>{mandate.kind.toUpperCase()}</span>
        </div>
        <div className="px-3 pb-3">
          <div className="flex items-start gap-2">
            <Info size={11} color={C.muted} className="flex-shrink-0 mt-0.5" />
            <span style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
              signature preview omitted — this identity is recognized, not cryptographically proven.
            </span>
          </div>
        </div>
      </motion.div>
    );
  }

  // 'unknown' / 'blocked' — MAJOR 1 fix (ADR-0159 security review). The
  // earlier revision rendered the animated fill bar + mulberry32 sig
  // preview + "SIGNED · NONCE VERIFIED · ANCHORED" line even when NO
  // registered key existed — the same visual overclaim the tier gate is
  // supposed to prevent. Suppress ALL signing-artifact chrome here and
  // render only the mono `ed25519 · NO KEY` label plus a static one-line
  // note in Sill voice. Mirror the 'identified' branch shape.
  if (tier !== 'verified') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        className="overflow-hidden"
        style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 3 }}
      >
        <div className="px-3 py-2 flex items-center gap-2 flex-wrap">
          <FileSignature size={11} color={C.muted} />
          <span style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1.5, fontWeight: 600 }}>{mandate.kind.toUpperCase()}</span>
        </div>
        <div className="px-3 pb-3">
          <div className="flex items-center gap-2 mb-1.5">
            <KeyRound size={9} color={C.muted} />
            <span style={{ fontFamily: monoFont, fontSize: 8, color: C.muted, letterSpacing: 1 }}>
              ed25519 · NO KEY
            </span>
          </div>
          <div className="flex items-start gap-2">
            <Info size={11} color={C.muted} className="flex-shrink-0 mt-0.5" />
            <span style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
              no signature to display — this identity has no registered key material.
            </span>
          </div>
        </div>
      </motion.div>
    );
  }

  // 'verified' — animated ed25519 art (djb2 / mulberry32 display artifact,
  // no cryptographic meaning). Only reachable when the registry row exists
  // AND `public_keys.length > 0`.
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="overflow-hidden"
      style={{ background: toneDim, border: `1px solid ${toneFg}`, borderRadius: 3 }}
    >
      <div className="px-3 py-2 flex items-center gap-2 flex-wrap" style={{ borderBottom: `1px solid ${toneFg}25` }}>
        <FileSignature size={11} color={toneFg} />
        <span style={{ fontFamily: monoFont, fontSize: 9, color: toneFg, letterSpacing: 1.5, fontWeight: 600 }}>{mandate.kind.toUpperCase()}</span>
        <span style={{ fontFamily: monoFont, fontSize: 11, color: C.text, marginLeft: 'auto' }}>{mandateId}</span>
      </div>

      <div className="px-3 py-2">
        <div className="flex items-center gap-2 mb-1.5">
          <KeyRound size={9} color={C.muted} />
          <span style={{ fontFamily: monoFont, fontSize: 8, color: C.muted, letterSpacing: 1 }}>
            ed25519 · {agent.id.toUpperCase()}_KEY_2026
          </span>
        </div>

        <div className="relative" style={{ height: 18, background: C.surface2, borderRadius: 2, overflow: 'hidden' }}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: signed ? '100%' : '0%' }}
            transition={{ duration: 0.6, ease: 'easeInOut' }}
            style={{ height: '100%', background: `linear-gradient(90deg, ${toneFg}50 0%, ${toneFg}80 100%)` }}
          />
          <div
            className="absolute inset-0 flex items-center px-2"
            style={{ fontFamily: monoFont, fontSize: 9, color: C.text, letterSpacing: 0.5, whiteSpace: 'nowrap', overflow: 'hidden' }}
          >
            {signed ? `${sigPreview.slice(0, 22)}…${sigPreview.slice(-8)}` : 'signing…'}
          </div>
        </div>

        {signed && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="flex items-center gap-1.5 mt-1.5">
            <Check size={9} color={C.limeFg} />
            <span style={{ fontFamily: monoFont, fontSize: 8, color: C.limeFg, letterSpacing: 1 }}>SIGNED · NONCE VERIFIED · ANCHORED</span>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}
